<template>
    <v-dialog
        v-model="isOpen"
        max-width="600px"
        :fullscreen="isFullscreen"
        :hide-overlay="isFullscreen"
        :transition="isFullscreen ? 'scroll-y-reverse-transition' : 'dialog-transition'"
    >
        <v-card>
            <v-card-title class="pa-0">
                <v-toolbar
                    dark
                    flat
                    color="primary"
                >
                    <v-btn
                        icon
                        dark
                        @click="isOpen = false"
                    >
                        <v-icon>fal fa-times</v-icon>
                    </v-btn>

                    <v-toolbar-title>
                        {{ i18n.t('connection.heading') }}
                    </v-toolbar-title>
                </v-toolbar>
            </v-card-title>

            <v-card-text class="px-4 py-2">
                <v-list>
                    <v-list-item :key="1" two-line>
                        <v-list-item-avatar :size="50">
                            <v-avatar
                                class="connection"
                                :class="{
                                    error: connectionsStatus.internet === 'error',
                                }"
                            >
                                <v-icon dark>fal fa-wifi</v-icon>
                            </v-avatar>
                        </v-list-item-avatar>

                        <v-list-item-content>
                            <v-list-item-title>{{ i18n.t('connection.internet') }}</v-list-item-title>
                            <v-list-item-subtitle v-if="connectionsStatus.internet === 'error'" class="error--text">
                                {{ i18n.t('connection.internet_offline_message') }}
                            </v-list-item-subtitle>

                            <v-list-item-subtitle v-else class="success--text">
                                {{ i18n.t('connection.internet_online_message') }}
                            </v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>

                    <v-divider v-if="showLocalConnection" />

                    <v-list-item
                        v-if="showLocalConnection"
                        :key="2"
                        two-line
                    >
                        <v-list-item-avatar :size="50">
                            <v-avatar
                                class="connection"
                                :class="{
                                    error: connectionsStatus.local === 'error',
                                    warning: connectionsStatus.local === 'warning'
                                }"
                            >
                                <v-icon dark>fal fa-network-wired</v-icon>
                            </v-avatar>
                        </v-list-item-avatar>

                        <v-list-item-content>
                            <v-list-item-title>{{ i18n.t('connection.local') }}</v-list-item-title>
                            <v-list-item-subtitle v-if="connectionsStatus.local === 'error'" class="error--text">
                                {{ i18n.t('connection.local_offline_message') }}
                            </v-list-item-subtitle>

                            <v-list-item-subtitle v-else-if="connectionsStatus.local === 'warning'" class="warning--text">
                                {{ i18n.t('connection.local_warning_message') }}
                            </v-list-item-subtitle>

                            <v-list-item-subtitle v-else class="success--text">
                                {{ i18n.t('connection.local_online_message') }}
                            </v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>

                <v-list v-if="showLocalConnection && store.getters.localConnection.terminals.length > 0">
                    <div class="body-1 px-4 mt-3">{{ i18n.tc('general.terminal', 2) }}:</div>

                    <template v-for="(ter, index) in store.getters.localConnection.terminals">
                        <v-list-item
                            :key="'trm' + index"
                            two-line
                        >
                            <v-list-item-avatar :size="50">
                                <v-avatar>
                                    <v-icon v-if="ter.terminal">{{ ter.terminal.platform === 'electron' ? 'far fa-desktop' : 'far fa-mobile' }}</v-icon>
                                    <v-icon v-else>far fa-question-circle</v-icon>
                                </v-avatar>
                            </v-list-item-avatar>

                            <v-list-item-content>
                                <v-list-item-title>{{ ter.terminal ? ter.terminal.name : '' }}</v-list-item-title>
                                <v-list-item-subtitle>{{ ter.address }}</v-list-item-subtitle>
                            </v-list-item-content>

                            <v-list-item-icon class="pt-3">
                                <v-icon v-if="ter.connected" color="success">fal fa-check</v-icon>
                                <v-icon v-else color="error">fal fa-times</v-icon>
                            </v-list-item-icon>
                        </v-list-item>

                        <v-divider v-if="index < store.getters.localConnection.terminals.length - 1" :key="'div' + index" />
                    </template>
                </v-list>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useAppStore } from '@/store/useAppStore';
import Terminal from '@/models/Terminal';
import i18n from '@/i18n';

const store = useAppStore()

const isOpen = ref(false)
const windowWidth = ref(window.innerWidth)
const terminals = ref([])
const terminal = ref()
const isWeb = computed(() => store.getters.device?.platform === 'web')
const isFullscreen = computed(() => windowWidth.value <= 500 || store.getters.device?.platform === 'android')

onMounted(async () => {
    EventBus.$on('show-connection-management', show)
})

onUnmounted(() => {
    EventBus.$off('show-connection-management', show)
})

const showLocalConnection = computed(() => !isWeb.value && !!terminal.value && terminals.value.some(t => t.isKitchenDisplaySystem()))
const hasConnectedTerminals = computed(() => store.getters.localConnection.terminals.filter(t => t.connected).length > 1)
const connectionsStatus = computed(() => {
    return {
        internet: store.getters.isOnline ? 'success' : 'error',
        local: !store.getters.localConnection.connected ? 'error' : (store.getters.localConnection.isServer && !hasConnectedTerminals.value ? 'warning' : 'success')
    }
})

async function show() {
    terminals.value = Terminal.all()
    terminal.value = await Terminal.current()
    windowWidth.value = window.innerWidth
    isOpen.value = true
}
</script>

<style lang="scss" scoped>
.connection {
    color: white;
    background: var(--v-success-base);

    &.error {
        background: var(--v-error-base);
        animation: blink 1.2s infinite;
    }

    &.warning {
        background: var(--v-warning-base);
    }

    @keyframes blink {
        0%, 100% { opacity: 1; }
        50% { opacity: 0; }
    }
}

.v-list-item__subtitle {
    white-space: normal;
}
</style>
